import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';
import { Button } from '@monksflow/monks-ui';
import { Pencil, Plus, Trash2, Waypoints } from 'lucide-react';
import {
  describeBranchAction,
  describeConditionGroup,
  getRuleBranches,
  type TaxonomyField,
  type TaxonomyRule,
} from '@/entities/taxonomy';
import { AddRuleModal } from '../AddRuleModal/AddRuleModal';

interface RulesEditorProps {
  fields: TaxonomyField[];
  rules: TaxonomyRule[];
  readOnly: boolean;
  onChange: (next: TaxonomyRule[]) => void;
}

/**
 * Rules tab: conditional field logic (SME). Each rule is a decision table of
 * branches — IF <nested AND/OR conditions> THEN <action> on a target field.
 * Rules are listed as readable IF/THEN cards; create/edit happens in
 * {@link AddRuleModal}.
 */
export function RulesEditor({ fields, rules, readOnly, onChange }: RulesEditorProps) {
  const { t } = useTranslation('knowledge');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TaxonomyRule | null>(null);

  const fieldName = (id: string) =>
    fields.find((f) => f.id === id)?.name ?? t('taxonomy.rulesEditor.field');

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (rule: TaxonomyRule) => {
    setEditing(rule);
    setModalOpen(true);
  };
  const saveRule = (rule: TaxonomyRule) => {
    const exists = rules.some((r) => r.id === rule.id);
    onChange(exists ? rules.map((r) => (r.id === rule.id ? rule : r)) : [...rules, rule]);
    setModalOpen(false);
    setEditing(null);
  };
  const removeRule = (id: string) => onChange(rules.filter((r) => r.id !== id));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {i18n.t('knowledge:taxonomy.rulesEditor.conditionalLogicLimitOptionsShow')}
        </p>
        {!readOnly && (
          <Button variant="outline" size="mini" className="h-8 text-xs" onClick={openAdd}>
            <Plus className="size-3.5" />
            {t('taxonomy.rulesEditor.addRule')}
          </Button>
        )}
      </div>

      {rules.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-light px-6 py-10 text-center">
          <Waypoints className="size-6 text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            {t('taxonomy.rulesEditor.noRulesYetAddRule')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {rules.map((rule) => {
            const branches = getRuleBranches(rule);
            return (
              <div
                key={rule.id}
                className="group flex items-start justify-between gap-3 rounded-lg border border-border-light bg-card p-3"
              >
                <div className="flex min-w-0 flex-col gap-2 text-sm">
                  {branches.map((branch, bi) => (
                    <div
                      key={branch.id}
                      className={`flex flex-col gap-1 ${bi > 0 ? 'border-t border-dashed border-border-light pt-2' : ''}`}
                    >
                      <div className="flex flex-wrap items-baseline gap-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {bi === 0
                            ? t('taxonomy.rulesEditor.if')
                            : t('taxonomy.rulesEditor.elseIf')}
                        </span>
                        <span className="rounded-md bg-surface px-1.5 py-0.5 font-mono text-xs text-foreground">
                          {describeConditionGroup(branch.when, fieldName)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('taxonomy.rulesEditor.then')}
                        </span>
                        <span className="text-foreground">
                          {describeBranchAction(branch, fieldName)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {!readOnly && (
                  <div className="flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <Button
                      variant="ghost"
                      size="mini"
                      aria-label={t('taxonomy.rulesEditor.editRule')}
                      className="size-7 p-0 text-muted-foreground"
                      onClick={() => openEdit(rule)}
                    >
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="mini"
                      aria-label={t('taxonomy.rulesEditor.deleteRule')}
                      className="size-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeRule(rule.id)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && (
        <AddRuleModal
          open
          fields={fields}
          rule={editing}
          onClose={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onSave={saveRule}
        />
      )}
    </div>
  );
}

RulesEditor.displayName = 'RulesEditor';
